import { Dropdown } from "primereact/dropdown";
import React, { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { useTranslation } from "react-i18next";
import { MdOutlineDeleteForever } from "react-icons/md";

import user_placeholder from "../../../assets/user-profile.jpg";
import { useTheme } from "../../../context/ThemeContext";
import { useSnack } from "../../../hooks/useSnack";
import classroomServices from "../../../services/classroomServices";
import schoolServices from "../../../services/schoolServices";
import teacherServices from "../../../services/teacherServices";
import Button from "../../Button";
import LoadingBtn from "../../LoadingBtn";
import LoadingSpinner from "../../LoadingSpinner";
import Modal from "../../Modal";

const DeleteTeacherAccountModal = forwardRef(({ onSubmit }, ref) => {
  const { theme } = useTheme();
  const { t } = useTranslation();
  const { showSnack } = useSnack();
  const [isOpen, setIsOpen] = useState(false);
  const [teacher, setTeacher] = useState(null);
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [selectedClassrooms, setSelectedClassrooms] = useState([]);
  const [teacherClassrooms, setTeacherClassrooms] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [loadingTeacherClassrooms, setLoadingTeacherClassrooms] = useState(false);
  const [loadingTeachers, setLoadingTeacher] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmittingReassign, setIsSubmittingReassign] = useState(false);

  useImperativeHandle(ref, () => ({
    openModal(teacher) {
      setTeacher(teacher);
      setIsOpen(true);
    },
    closeModal() {
      setIsOpen(false);
    },
  }));

  const handleClassroomSelection = (classroomId) => {
    setSelectedClassrooms((prev) =>
      prev.includes(classroomId) ? prev.filter((id) => id !== classroomId) : [...prev, classroomId]
    );
  };

  const handleConfirm = async () => {
    setIsSubmitting(true);
    try {
      const res = await teacherServices.deleteTeacher(teacher?.id);
      showSnack(res.message, "success");
      onSubmit();
      setIsOpen(false);
      setTimeout(() => {
        setIsSubmitting(false);
      }, 500);
    } catch (error) {
      setIsSubmitting(false);
      showSnack(error, "error");
    }
  };

  const handleReassign = async () => {
    setIsSubmittingReassign(true);
    try {
      const payload = {
        teacher_id: selectedTeacher,
        classrooms: selectedClassrooms.map((classroom) => classroom),
      };

      const response = await classroomServices.reassignMultiClassrooms(payload);
      showSnack(response.message, "success");
      setSelectedClassrooms([]);
      getTeacherClassrooms();
      setTimeout(() => {
        setIsSubmittingReassign(false);
      }, 500);
    } catch (error) {
      setIsSubmittingReassign(false);
      showSnack(error, "error");
    }
  };

  const getTeacherClassrooms = async () => {
    setLoadingTeacherClassrooms(true);
    try {
      const response = await teacherServices.getClassroomsByTeacher(teacher?.id, { has_students: true });
      setTeacherClassrooms(response.data);
    } catch (error) {
    } finally {
      setLoadingTeacherClassrooms(false);
    }
  };

  const getTeachers = async () => {
    setLoadingTeacher(true);
    try {
      const response = await schoolServices.getTeachersBySchool();
      const filteredTeachers = response.data
        .filter((item) => item.id !== teacher.id && !item?.is_disabled) // Exclude current teacher and disabled ones
        .map((teacher) => ({
          label: teacher.name,
          value: teacher.id,
        }));

      setTeachers(filteredTeachers);
    } catch (error) {
      console.error("Error fetching teachers data:", error);
    } finally {
      setLoadingTeacher(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      if (teacher?.classrooms_count) {
        getTeacherClassrooms();
        getTeachers();
      }
      setSelectedTeacher(null);
      setSelectedClassrooms([]);
      setTeacherClassrooms([]);
      setTeachers([]);
    }
  }, [isOpen]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => setIsOpen(false)}
      header={
        <div className="flex items-center justify-between">
          <p className="flex items-center gap-2 text-red-500">
            <MdOutlineDeleteForever size={22} className="text-red-500" />
            {t("delete")} {t("the_account")}
          </p>
        </div>
      }
      footer={
        <div className="flex items-center justify-end space-x-2">
          <Button color="white" variant="outlined" onClick={() => setIsOpen(false)} label={t("cancel")} />

          {!teacher?.is_disabled && selectedClassrooms.length > 0 && selectedTeacher && (
            <Button
              disabled={!selectedTeacher || isSubmittingReassign}
              onClick={handleReassign}
              className={`mt-3 inline-flex w-full min-w-28 justify-center rounded-md ${isSubmittingReassign ? "py-[3px]" : "py-2"} bg-[#FF602B] px-5 py-2 text-sm font-semibold text-white shadow-md transition duration-300 hover:scale-[1.02] hover:bg-[#FF602B] sm:mt-0 sm:w-auto`}
            >
              {isSubmittingReassign ? <LoadingBtn color={"white"} /> : t("reassign")}
            </Button>
          )}
          <Button
            onClick={handleConfirm}
            disabled={
              isSubmitting
                ? true
                : teacher?.is_disabled
                  ? false // Button is enabled when teacher is disabled
                  : teacherClassrooms.length > 0 || loadingTeacherClassrooms || loadingTeachers
            }
            className={`inline-flex w-full min-w-28 ${isSubmitting ? "py-[3px]" : "py-2"} justify-center rounded-md px-5 py-2 text-sm font-semibold text-white shadow-md transition duration-300 sm:mt-0 sm:w-auto ${
              isSubmitting
                ? "cursor-not-allowed bg-gray-500" // Keep bg-gray-500 when submitting
                : teacher?.is_disabled
                  ? "bg-red-500 hover:scale-[1.02] hover:bg-red-600"
                  : teacherClassrooms.length > 0 || loadingTeacherClassrooms || loadingTeachers
                    ? "cursor-not-allowed bg-gray-400"
                    : "bg-red-500 hover:scale-[1.02] hover:bg-red-600"
            }`}
          >
            {isSubmitting ? (
              <LoadingBtn color={"white"} />
            ) : !teacher?.is_disabled ? (
              t("delete")
            ) : (
              t("delete")
            )}{" "}
          </Button>
        </div>
      }
    >
      {/* Modal Body Content */}
      <>
        <div className="flex items-center justify-between gap-6">
          <div className="flex w-64 flex-col items-center justify-center gap-2 rounded-xl bg-blue-200 p-3 py-5 dark:bg-dark-background">
            <img
              className="mb-2 h-14 w-14 rounded-full object-cover"
              src={teacher?.image || user_placeholder}
              alt="user"
            />{" "}
            <p className="text-center text-sm dark:text-gray-100">{teacher?.name}</p>
          </div>
          <p className="text-start text-mutedText dark:text-dark-mutedText">
            {t("are_you_sure")}{" "}
            <span className="font-medium">
              {t("delete")} {teacher?.name} {t("question_mark")}
            </span>
          </p>
        </div>
        {loadingTeacherClassrooms ? (
          <LoadingSpinner />
        ) : (
          !teacher?.is_disabled &&
          teacherClassrooms.length > 0 && (
            <div className="mt-6">
              <h3 className="mb-2 text-start text-sm font-semibold text-red-500">
                * {t("assign_classrooms_to_another_teacher")}
              </h3>
              <div className="relative max-h-40 overflow-y-auto rounded-md border p-2 dark:border-gray-600">
                {/* Select All Option */}
                <label className="mb-2 flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={selectedClassrooms.length === teacherClassrooms.length}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedClassrooms(teacherClassrooms.map((classroom) => classroom.id)); // Select all classrooms
                      } else {
                        setSelectedClassrooms([]); // Deselect all classrooms
                      }
                    }}
                    className="h-4 w-4 cursor-pointer"
                  />
                  <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                    {t("select_all")}
                  </span>
                </label>

                {/* Classrooms List */}
                {teacherClassrooms.map((classroom) => (
                  <label key={classroom.id} className="mb-2 flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={selectedClassrooms.includes(classroom.id)}
                      onChange={() => handleClassroomSelection(classroom.id)}
                      className="h-4 w-4 cursor-pointer"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-200">{classroom.name}</span>
                  </label>
                ))}
              </div>
              <Dropdown
                pt={{
                  wrapper: "text-sm",
                  header: "bg-blue-200 p-2 rounded-t-md",
                  input:
                    "p-0 text-sm placeholder:text-sm placeholder:text-mutedText dark:text-dark-mutedText dark:text-white",
                  root: "w-full rounded-md border bg-[#F9FAFB] px-3 py-2 focus:border-green-500 focus:ring focus:ring-green-500 focus:ring-opacity-10 dark:border-dark-background dark:bg-dark-background",
                  panel:
                    "bg-white dark:bg-gray-800 shadow-lg rounded-md border border-gray-200 dark:border-gray-700",
                  item: "p-2 hover:bg-green-100 dark:hover:bg-green-700",
                }}
                value={selectedTeacher}
                onChange={(e) => setSelectedTeacher(e.value)}
                options={teachers}
                optionLabel="label"
                optionValue="value"
                placeholder={t("select_teacher")}
                className="w-full"
                loading={loadingTeachers}
                filter
                emptyFilterMessage={t("no_results_found")}
              />
            </div>
          )
        )}
      </>
    </Modal>
  );
});

export default DeleteTeacherAccountModal;
